import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { api, API } from "../lib/api";
import { logDev } from "../lib/log";

const TOAST_ID = "connection-monitor";
const POLL_MS = 3000;

/**
 * Watches axios responses for network errors / 5xx outages and shows a
 * friendly "Reconnecting…" toast while the backend is unreachable.
 *
 * On the first failed response we mark the app as offline and start polling
 * /api/health every 3 seconds. As soon as the probe succeeds we flip back to
 * online and show a short "Back online" confirmation.
 */
export default function ConnectionMonitor() {
  const offline = useRef(false);
  const pollTimer = useRef(null);

  useEffect(() => {
    const setOffline = () => {
      if (offline.current) return;
      offline.current = true;
      toast.loading("Reconnecting to liveprocure.com…", {
        id: TOAST_ID,
        duration: Infinity,
        description: "Hold tight — checking your connection.",
      });
      if (!pollTimer.current) {
        pollTimer.current = setInterval(probe, POLL_MS);
      }
    };

    const setOnline = () => {
      if (!offline.current) return;
      offline.current = false;
      if (pollTimer.current) {
        clearInterval(pollTimer.current);
        pollTimer.current = null;
      }
      toast.success("Back online", { id: TOAST_ID, duration: 2200 });
    };

    const probe = async () => {
      try {
        // Use fetch (not the axios instance) so this probe stays outside the
        // interceptor and never triggers another offline cycle on its own.
        const res = await fetch(`${API}/health`, { credentials: "include" });
        if (res.ok) setOnline();
      } catch (e) {
        logDev("connection.probe", e);
        // still offline — keep polling
      }
    };

    const isTransport = (err) => {
      if (!err) return false;
      // No HTTP response at all = transport / DNS / CORS / refused connection.
      if (!err.response) return true;
      const s = err.response.status;
      return s === 502 || s === 503 || s === 504;
    };

    const interceptorId = api.interceptors.response.use(
      (resp) => {
        // Any successful API response implies we are online.
        setOnline();
        return resp;
      },
      (err) => {
        if (isTransport(err)) setOffline();
        return Promise.reject(err);
      },
    );

    const handleOffline = () => setOffline();
    const handleOnline = () => probe();
    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);

    return () => {
      api.interceptors.response.eject(interceptorId);
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
      if (pollTimer.current) {
        clearInterval(pollTimer.current);
        pollTimer.current = null;
      }
    };
  }, []);

  return null;
}
